import type { ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { ErrorState } from '../../components/ui/Feedback';
import { DetailSkeleton } from '../../components/ui/Skeleton';
import { openAuthenticatedRoute } from '../../lib/auth';
import { ApiError } from '../../lib/api-client';
import { formatDate, formatDateTime } from '../../lib/datetime';
import { usePatientProfile } from './patient.queries';
import type {
  PatientHistoryRecord,
  PatientProfileBill,
  PatientProfileTest,
  PatientProfilePrescription,
  PatientReference,
} from './types';
import './patient-profile.css';

const currencyFormatter = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  maximumFractionDigits: 2,
});

const currency = (value: number | undefined) => currencyFormatter.format(value ?? 0);

const dateTime = (value: string | null | undefined) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : formatDateTime(date);
};

const dateOnly = (value: string | null | undefined) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : formatDate(date);
};

const ageFrom = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  const today = new Date();
  let age = today.getFullYear() - date.getFullYear();
  if (today.getMonth() < date.getMonth() || (today.getMonth() === date.getMonth() && today.getDate() < date.getDate())) age -= 1;
  return `${Math.max(0, age)} years`;
};

const readable = (value: string | null | undefined) => (
  value ? value.replaceAll('-', ' ').replace(/\b\w/g, (letter) => letter.toUpperCase()) : '—'
);

const newestFirst = (left: string | undefined, right: string | undefined) => (
  new Date(right ?? 0).getTime() - new Date(left ?? 0).getTime()
);

const referenceName = (reference: PatientReference | null | undefined) => reference?.name || '—';

const prescriptionPrintPath = (prescription: PatientProfilePrescription, withHeader: boolean) => {
  const base = prescription.source === 'OPD'
    ? `/opd/${prescription.recordId}/print-prescription`
    : prescription.source === 'EMG'
      ? `/emg/${prescription.recordId}/print-prescription`
      : `/ipd-daycare/${prescription.recordId}/print-prescription`;
  return `${base}?header=${withHeader ? '1' : '0'}`;
};

const DetailsGrid = ({ items }: { items: Array<[string, ReactNode]> }) => (
  <dl className="patient-profile-details-grid">
    {items.map(([label, value]) => (
      <div key={label}>
        <dt>{label}</dt>
        <dd>{value === null || value === undefined || value === '' ? '—' : value}</dd>
      </div>
    ))}
  </dl>
);

const Status = ({ value }: { value: string | null | undefined }) => (
  <span className={`patient-profile-status status-${value ?? 'unknown'}`}>{readable(value)}</span>
);

const NoRecords = ({ label }: { label: string }) => (
  <div className="patient-profile-no-records">No {label.toLowerCase()} recorded for this patient.</div>
);

const Section = ({ id, title, count, children }: { id: string; title: string; count: number; children: ReactNode }) => (
  <section className="patient-profile-section" id={id}>
    <header>
      <h2>{title}</h2>
      <span>{count}</span>
    </header>
    <div className="patient-profile-section-body">{children}</div>
  </section>
);

const BillCard = ({ bill }: { bill: PatientProfileBill }) => (
  <details className="patient-profile-record">
    <summary>
      <div>
        <strong>{bill.source} bill {bill.billNumber || `#${bill.referenceNumber}`}</strong>
        <span>{dateTime(bill.date)} · {referenceName(bill.doctor)}</span>
      </div>
      <div className="patient-profile-record-total">
        <strong>{currency(bill.billing.grandTotal)}</strong>
        <Status value={bill.billing.billStatus} />
      </div>
    </summary>
    <div className="patient-profile-record-content">
      <DetailsGrid items={[
        ['Module', bill.source],
        ['Record number', bill.referenceNumber],
        ['Bill number', bill.billNumber],
        ['Bill date', dateTime(bill.date)],
        ['Doctor', referenceName(bill.doctor)],
        ['Approval', bill.billing.approved ? 'Approved' : 'Not approved'],
        ['Gross total', currency(bill.billing.total)],
        ['Discount', `${currency(bill.billing.totalDiscount)} (${readable(bill.billing.discountType)})`],
        ['Grand total', currency(bill.billing.grandTotal)],
        ['Paid', currency(bill.billing.totalPayment)],
        ['Due', currency(bill.billing.dueAmount)],
        ['Credit', currency(bill.billing.creditAmount)],
      ]} />
      <h3>Charge details</h3>
      {bill.charges.length > 0 ? (
        <div className="patient-profile-table-wrap">
          <table>
            <thead><tr><th>Charge / test</th><th>Rate</th><th>Qty</th><th>Discount</th><th>Amount</th></tr></thead>
            <tbody>{bill.charges.map((charge, index) => (
              <tr key={`${charge.chargeName}-${index}`}>
                <td>{charge.chargeName}</td><td>{currency(charge.rate)}</td><td>{charge.quantity}</td>
                <td>{currency(charge.discountAmount)}</td><td>{currency(charge.amount)}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      ) : <NoRecords label="charge lines" />}
      <h3>Payment details</h3>
      {bill.payments.length > 0 ? (
        <div className="patient-profile-payments">
          {bill.payments.map((payment, index) => (
            <div key={`${payment.mode}-${index}`}><span>{payment.mode}{payment.bankName ? ` · ${payment.bankName}` : ''}</span><strong>{currency(payment.amount)}</strong></div>
          ))}
        </div>
      ) : <NoRecords label="payments" />}
      <Link className="patient-profile-record-link" to={bill.href}>Open original record →</Link>
    </div>
  </details>
);

const TestCard = ({ test }: { test: PatientProfileTest }) => (
  <details className="patient-profile-record">
    <summary>
      <div><strong>{test.name}</strong><span>Investigation #{test.investigationNumber} · {dateTime(test.date)}</span></div>
      <Status value={test.status} />
    </summary>
    <div className="patient-profile-record-content">
      <DetailsGrid items={[
        ['Investigation number', test.investigationNumber],
        ['Bill number', test.billNumber],
        ['Barcode', test.barcode],
        ['Amount', currency(test.amount)],
        ['Ordered', dateTime(test.orderedAt)],
        ['Sample collected', dateTime(test.sampleCollectedAt)],
        ['Received in lab', dateTime(test.labReceivedAt)],
        ['Report generated', dateTime(test.reportGeneratedAt)],
      ]} />
      <Link className="patient-profile-record-link" to={test.href}>Open investigation bill →</Link>
    </div>
  </details>
);

const PrescriptionCard = ({ prescription }: { prescription: PatientProfilePrescription }) => (
  <details className="patient-profile-record">
    <summary>
      <div><strong>{prescription.source} prescription #{prescription.recordNumber}</strong><span>{dateTime(prescription.prescriptionDate)} · {referenceName(prescription.createdBy)}</span></div>
      <span>{prescription.medicines.length} {prescription.medicines.length === 1 ? 'medicine' : 'medicines'}</span>
    </summary>
    <div className="patient-profile-record-content">
      <DetailsGrid items={[
        ['Type', prescription.source], ['Record number', prescription.recordNumber], ['Prescription date', dateTime(prescription.prescriptionDate)],
        ['Prescribed by', referenceName(prescription.createdBy)], ['Diagnosis', prescription.diagnoses.join(', ')],
        ['Complaints', prescription.complaints.map((item) => item.complaint).filter(Boolean).join(', ')],
        ['Tests needed', prescription.testsNeeded], ['Advice', prescription.advice],
      ]} />
      <h3>Medicines</h3>
      {prescription.medicines.length > 0 ? <div className="patient-profile-table-wrap"><table><thead><tr><th>Medicine</th><th>Dose</th><th>When</th><th>Frequency</th><th>Duration</th></tr></thead><tbody>{prescription.medicines.map((medicine, index) => <tr key={`${medicine.medicine}-${index}`}><td>{medicine.medicine || medicine.composition || '—'}</td><td>{medicine.dose || '—'}</td><td>{medicine.when || '—'}</td><td>{medicine.frequency || '—'}</td><td>{medicine.duration || '—'}</td></tr>)}</tbody></table></div> : <NoRecords label="medicines" />}
      <div className="patient-profile-print-actions"><button type="button" onClick={() => openAuthenticatedRoute(prescriptionPrintPath(prescription, false))}>Print without letterhead</button><button type="button" onClick={() => openAuthenticatedRoute(prescriptionPrintPath(prescription, true))}>Print with letterhead</button></div>
      <Link className="patient-profile-record-link" to={prescription.editHref}>Open prescription record →</Link>
    </div>
  </details>
);

const AppointmentCard = ({ row }: { row: PatientHistoryRecord }) => (
  <details className="patient-profile-record">
    <summary>
      <div><strong>Appointment #{row.enquiryNumber}</strong><span>{dateTime(row.appointmentDate)} · {referenceName(row.doctor)}</span></div>
      <Status value={row.status} />
    </summary>
    <div className="patient-profile-record-content">
      <DetailsGrid items={[
        ['Appointment date', dateTime(row.appointmentDate)],
        ['Booking time', dateTime(row.bookingTime)],
        ['Department', referenceName(row.department)],
        ['Doctor', referenceName(row.doctor)],
        ['Slot', referenceName(row.slot)],
        ['Booked by', referenceName(row.bookingBy)],
        ['Patient phone at booking', row.patientSnapshot?.phone],
        ['Address at booking', [row.patientSnapshot?.address, row.patientSnapshot?.district, row.patientSnapshot?.state, row.patientSnapshot?.pinCode].filter(Boolean).join(', ')],
      ]} />
      <Link className="patient-profile-record-link" to="/opd/appointments">Open appointments →</Link>
    </div>
  </details>
);

const VisitCard = ({ row, kind }: { row: PatientHistoryRecord; kind: 'OPD' | 'Emergency' }) => {
  const isOpd = kind === 'OPD';
  const href = isOpd ? `/opd/${row.id}` : `/emg/${row.id}`;
  return (
    <details className="patient-profile-record">
      <summary>
        <div>
          <strong>{kind} visit #{isOpd ? row.opdNumber : row.emgNumber}</strong>
          <span>{dateTime(isOpd ? row.appointmentDate : row.registrationDate)} · {referenceName(isOpd ? row.doctor : row.attendingDoctor)}</span>
        </div>
        <Status value={row.billing?.billStatus} />
      </summary>
      <div className="patient-profile-record-content">
        <DetailsGrid items={[
          ['Visit type', readable(row.type)],
          ['Visit date', dateTime(isOpd ? row.appointmentDate : row.registrationDate)],
          ['Ticket number', row.ticketNumber],
          ['Department', referenceName(row.department)],
          ['Doctor', referenceName(isOpd ? row.doctor : row.attendingDoctor)],
          ['Referred by', referenceName(row.referredBy)],
          ['Provider', referenceName(row.provider)],
          ['Marketed by', referenceName(row.marketBy)],
          ['Generated by', referenceName(row.generatedBy)],
          ['Bill number', row.billing?.billNumber],
          ['Grand total', currency(row.billing?.grandTotal)],
          ['Paid', currency(row.billing?.totalPayment)],
          ['Due', currency(row.billing?.dueAmount)],
        ]} />
        <Link className="patient-profile-record-link" to={href}>Open full {kind} record →</Link>
      </div>
    </details>
  );
};

const AdmissionCard = ({ row, kind }: { row: PatientHistoryRecord; kind: 'IPD / Daycare' | 'Dialysis' }) => {
  const isDialysis = kind === 'Dialysis';
  const href = isDialysis ? `/dialysis/${row.id}` : `/ipd-daycare/${row.id}`;
  return (
    <details className="patient-profile-record">
      <summary>
        <div>
          <strong>{isDialysis ? `Dialysis #${row.dialysisNumber}` : `${row.admissionType} #${row.ipdNumber}`}</strong>
          <span>{dateTime(row.admissionDate)} · {referenceName(row.underDoctor)}</span>
        </div>
        <Status value={row.status} />
      </summary>
      <div className="patient-profile-record-content">
        <DetailsGrid items={[
          ['Admission date', dateTime(row.admissionDate)],
          ['Visit type', readable(row.visitType)],
          ['Under doctor', referenceName(row.underDoctor)],
          ['Other doctor', referenceName(row.otherUnderDoctor)],
          ['Ward', referenceName(row.bedCategory)],
          ['Bed', referenceName(row.bed)],
          ['Dialysis machine', referenceName(row.dialysisMachine)],
          ['Timing', row.startTime && row.endTime ? `${row.startTime} – ${row.endTime}` : null],
          ['Package', row.packageType],
          ['Insurance', [row.insuranceType, row.insuranceNumber].filter(Boolean).join(' · ')],
          ['Symptoms', row.symptoms ? <span dangerouslySetInnerHTML={{ __html: row.symptoms }} /> : null],
          ['Diagnosis at admission', row.diagnosisAtAdmission ? <span dangerouslySetInnerHTML={{ __html: row.diagnosisAtAdmission }} /> : null],
          ['Alcohol / tobacco history', row.alcoholTobaccoHistory ? <span dangerouslySetInnerHTML={{ __html: row.alcoholTobaccoHistory }} /> : null],
          ['Past medical / surgical history', row.pastMedicalSurgicalHistory ? <span dangerouslySetInnerHTML={{ __html: row.pastMedicalSurgicalHistory }} /> : null],
          ['Family history', row.familyHistory ? <span dangerouslySetInnerHTML={{ __html: row.familyHistory }} /> : null],
          ['Responsible person', [row.responsiblePerson?.name, row.responsiblePerson?.relation, row.responsiblePerson?.phone].filter(Boolean).join(' · ')],
          ['Referred by', referenceName(row.referredBy)],
          ['Generated by', referenceName(row.generatedBy)],
        ]} />
        <Link className="patient-profile-record-link" to={href}>Open full admission →</Link>
      </div>
    </details>
  );
};

export const PatientProfilePage = () => {
  const { patientId = '' } = useParams();
  const navigate = useNavigate();
  const profileQuery = usePatientProfile(patientId);

  if (profileQuery.isLoading) return <DetailSkeleton label="Loading patient profile" />;
  if (profileQuery.isError || !profileQuery.data) {
    return (
      <ErrorState
        title="Unable to load patient profile"
        message={profileQuery.error instanceof ApiError ? profileQuery.error.message : 'The patient record could not be loaded.'}
        action={{ label: 'Try again', onClick: () => void profileQuery.refetch() }}
      />
    );
  }

  const profile = profileQuery.data;
  const patient = profile.patient;
  const fullName = `${patient.firstName} ${patient.lastName}`.trim();
  const address = [patient.address, patient.district, patient.state, patient.pinCode].filter(Boolean).join(', ');
  const totalVisits = profile.summary.opdVisits + profile.summary.emergencyVisits;
  const visits = [
    ...profile.opdRegistrations.map((row) => ({ row, kind: 'OPD' as const, date: row.appointmentDate })),
    ...profile.emgRegistrations.map((row) => ({ row, kind: 'Emergency' as const, date: row.registrationDate })),
  ].sort((left, right) => newestFirst(left.date, right.date));
  const admissions = [
    ...profile.ipdDaycareAdmissions.map((row) => ({ row, kind: 'IPD / Daycare' as const })),
    ...profile.dialysisAdmissions.map((row) => ({ row, kind: 'Dialysis' as const })),
  ].sort((left, right) => newestFirst(left.row.admissionDate, right.row.admissionDate));
  const bloodAndBedHistory = [
    ...profile.bloodIssues.map((row) => ({ row, kind: 'blood' as const, date: row.issueDate })),
    ...profile.bedOccupancies.map((row) => ({ row, kind: 'bed' as const, date: row.admittedAt })),
  ].sort((left, right) => newestFirst(left.date, right.date));

  return (
    <div className="page-stack patient-profile-page">
      <section className="patient-profile-hero">
        <div className="patient-profile-avatar" aria-hidden="true">{patient.firstName.charAt(0)}{patient.lastName.charAt(0)}</div>
        <div className="patient-profile-identity">
          <p>Patient master profile</p>
          <h1>{fullName}</h1>
          <div><strong>UHID {patient.medicalRecordNumber}</strong><span>{readable(patient.status)}</span></div>
        </div>
        <div className="patient-profile-hero-meta">
          <span>Last activity</span><strong>{dateTime(profile.summary.lastActivityAt)}</strong>
          <button
            type="button"
            className="button button-secondary"
            onClick={() => {
              const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0;
              if (idx > 0) void navigate(-1);
              else void navigate('/patients');
            }}
          >
            Back
          </button>
        </div>
      </section>

      <nav className="patient-profile-nav" aria-label="Patient profile sections">
        <a href="#activity">Activity</a><a href="#appointments">Appointments</a><a href="#bills">Bills</a>
        <a href="#tests">Tests</a><a href="#visits">Visits</a><a href="#admissions">Admissions</a>
        <a href="#prescriptions">Prescriptions</a><a href="#procedures">OT</a><a href="#blood-bed">Blood &amp; bed</a>
      </nav>

      <section className="patient-profile-demographics">
        <header><h2>Patient details</h2><span>Master demographics and contacts</span></header>
        <DetailsGrid items={[
          ['UHID', patient.medicalRecordNumber],
          ['Full name', fullName],
          ['Date of birth', dateOnly(patient.dateOfBirth)],
          ['Age', ageFrom(patient.dateOfBirth)],
          ['Gender', readable(patient.gender)],
          ['Blood group', patient.bloodGroup],
          ['Phone', patient.phone],
          ['Email', patient.email],
          ['Address', address],
          ['Allergies', patient.allergies.length > 0 ? patient.allergies.join(', ') : 'None recorded'],
          ['Emergency contact', patient.emergencyContact ? `${patient.emergencyContact.name} · ${patient.emergencyContact.relationship} · ${patient.emergencyContact.phone}` : null],
          ['Registered on', dateTime(patient.createdAt)],
        ]} />
      </section>

      <div className="patient-profile-stats">
        {[
          ['Appointments', profile.summary.appointments], ['Visits', totalVisits], ['Tests', profile.summary.tests],
          ['Admissions', profile.summary.admissions], ['Dialysis', profile.summary.dialysis], ['OT cases', profile.summary.otCases],
          ['Blood issues', profile.summary.bloodIssues], ['Bills', profile.summary.bills],
        ].map(([label, value]) => <div key={label}><strong>{value}</strong><span>{label}</span></div>)}
      </div>

      <section className="patient-profile-financials">
        <div><span>Total billed</span><strong>{currency(profile.summary.billed)}</strong></div>
        <div><span>Total paid</span><strong>{currency(profile.summary.paid)}</strong></div>
        <div className={profile.summary.due > 0 ? 'due' : ''}><span>Total due</span><strong>{currency(profile.summary.due)}</strong></div>
      </section>

      <Section id="activity" title="Complete activity timeline" count={profile.timeline.length}>
        {profile.timeline.length === 0 ? <NoRecords label="activity" /> : (
          <ol className="patient-profile-timeline">
            {profile.timeline.map((item) => (
              <li key={item.id}>
                <span className={`patient-profile-timeline-dot type-${item.type}`} />
                <div><small>{readable(item.type)}</small><strong>{item.title}</strong>{item.subtitle ? <span>{item.subtitle}</span> : null}</div>
                <div className="patient-profile-timeline-meta"><time>{dateTime(item.date)}</time>{item.status ? <Status value={item.status} /> : null}{item.href ? <Link to={item.href}>Open →</Link> : null}</div>
              </li>
            ))}
          </ol>
        )}
      </Section>

      <Section id="appointments" title="Appointments" count={profile.appointments.length}>
        {profile.appointments.length === 0 ? <NoRecords label="appointments" /> : profile.appointments.map((row) => <AppointmentCard key={row.id} row={row} />)}
      </Section>

      <Section id="bills" title="All bills and payments" count={profile.bills.length}>
        {profile.bills.length === 0 ? <NoRecords label="bills" /> : profile.bills.map((bill) => <BillCard key={bill.id} bill={bill} />)}
      </Section>

      <Section id="tests" title="Tests and investigations" count={profile.tests.length}>
        {profile.tests.length === 0 ? <NoRecords label="tests" /> : profile.tests.map((test) => <TestCard key={test.id} test={test} />)}
      </Section>

      <Section id="visits" title="OPD and emergency visits" count={totalVisits}>
        {totalVisits === 0 ? <NoRecords label="visits" /> : (
          <>{visits.map(({ row, kind }) => <VisitCard key={`${kind}-${row.id}`} row={row} kind={kind} />)}</>
        )}
      </Section>

      <Section id="prescriptions" title="Prescriptions" count={profile.prescriptions.length}>
        {profile.prescriptions.length === 0 ? <NoRecords label="prescriptions" /> : profile.prescriptions.map((row) => <PrescriptionCard key={row.id} prescription={row} />)}
      </Section>

      <Section id="admissions" title="IPD, daycare and dialysis admissions" count={profile.ipdDaycareAdmissions.length + profile.dialysisAdmissions.length}>
        {profile.ipdDaycareAdmissions.length + profile.dialysisAdmissions.length === 0 ? <NoRecords label="admissions" /> : (
          <>{admissions.map(({ row, kind }) => <AdmissionCard key={`${kind}-${row.id}`} row={row} kind={kind} />)}</>
        )}
      </Section>

      <Section id="procedures" title="Operation theatre procedures" count={profile.otCases.length}>
        {profile.otCases.length === 0 ? <NoRecords label="OT procedures" /> : profile.otCases.map((row) => (
          <details className="patient-profile-record" key={row.id}>
            <summary><div><strong>OT case #{row.otNumber} · {referenceName(row.procedureType)}</strong><span>{dateTime(row.scheduledDate)} · {referenceName(row.surgeon)}</span></div><Status value={row.status} /></summary>
            <div className="patient-profile-record-content">
              <DetailsGrid items={[
                ['Scheduled date', dateTime(row.scheduledDate)], ['Procedure', referenceName(row.procedureType)], ['OT room', referenceName(row.otRoom)],
                ['Surgeon', referenceName(row.surgeon)], ['Anesthetist', referenceName(row.anesthetist)], ['Timing', row.startTime && row.endTime ? `${row.startTime} – ${row.endTime}` : null],
                ['Diagnosis', row.diagnosis], ['Notes', row.notes], ['Generated by', referenceName(row.generatedBy)],
              ]} />
              <Link className="patient-profile-record-link" to={`/ot/${row.id}`}>Open full OT record →</Link>
            </div>
          </details>
        ))}
      </Section>

      <Section id="blood-bed" title="Blood issues and bed history" count={profile.bloodIssues.length + profile.bedOccupancies.length}>
        {profile.bloodIssues.length + profile.bedOccupancies.length === 0 ? <NoRecords label="blood or bed history" /> : (
          <>
            {bloodAndBedHistory.map(({ row, kind }) => kind === 'blood' ? (
              <details className="patient-profile-record" key={`blood-${row.id}`}>
                <summary><div><strong>Blood issue {row.issueNumber}</strong><span>{dateTime(row.issueDate)} · Bag {row.bagBarcode}</span></div><Status value={row.crossmatchResult} /></summary>
                <div className="patient-profile-record-content"><DetailsGrid items={[
                  ['Issue date', dateTime(row.issueDate)], ['Section', row.section],
                  ['Bag barcode', row.bagBarcode], ['Donor group', row.donorGroup], ['Recipient group', row.recipientGroup], ['Crossmatch', row.crossmatchResult],
                  ['Donation', referenceName(row.donation)], ['Issued by', referenceName(row.issuedBy)], ['Note', row.note],
                ]} /><Link className="patient-profile-record-link" to="/blood-bank/issues">Open blood issues →</Link></div>
              </details>
            ) : (
              <details className="patient-profile-record" key={`bed-${row.id}`}>
                <summary><div><strong>{readable(row.source)} bed stay · {referenceName(row.bed)}</strong><span>{dateTime(row.admittedAt)}{row.releasedAt ? ` to ${dateTime(row.releasedAt)}` : ' · Current stay'}</span></div><Status value={row.active ? 'active' : 'released'} /></summary>
                <div className="patient-profile-record-content"><DetailsGrid items={[
                  ['Bed', referenceName(row.bed)], ['Usage', readable(row.usageStatus)], ['Source', readable(row.source)], ['Source record', row.sourceRecordId],
                  ['Admitted', dateTime(row.admittedAt)], ['Released', dateTime(row.releasedAt)], ['Patient number', row.patientNumber],
                ]} /><Link className="patient-profile-record-link" to="/bed-status">Open bed status →</Link></div>
              </details>
            ))}
          </>
        )}
      </Section>
    </div>
  );
};
